/**
 * Dados de WhatsApi › Conversas (spec 2026-11-02 conversas §5.7). Nas fatias 1+2 a tela relê
 * sozinha: a lista a cada 5 s, a conversa aberta a cada 3 s, e as duas ao focar a janela.
 * Devolver ao bot é o `useDevolverConversa` da seção do gateway: uma forma só.
 */

import { useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import {
  whatsapiOrgApi, type ConversaWhatsApp, type ListaDeConversas, type MensagemDaConversa,
  type PaginaDeMensagens,
} from '@/api/whatsapiOrg'
import { mensagemDeErro } from '@/components/admin/whatsappGateways/mensagens'

const RELEITURA_DA_LISTA_MS = 5_000
const RELEITURA_DA_CONVERSA_MS = 3_000

/**
 * Primeira página relida no intervalo ("cabeça") e as anteriores, pedidas por "Carregar mais",
 * fora dele ("cauda"): a consulta infinita releria TODAS as páginas carregadas a cada volta.
 * A cauda começa no `proximo` (cursor opaco, nunca lido aqui) que a cabeça tinha no clique; o
 * que aparece nas duas fica com a versão da cabeça, que é a mais nova. A cauda só se relê
 * depois de uma ação (invalidação), e um item que a cabeça empurrou para fora depois do clique
 * só volta quando recebe mensagem nova ou a tela é reaberta.
 */
function useCabecaECauda<P extends { proximo: string | null; items: I[] }, I>({
  chave, buscar, intervalo, ligado, idDe,
}: {
  chave: unknown[]
  buscar: (antesDe: string | null) => Promise<P>
  intervalo: number
  ligado: boolean
  idDe: (item: I) => string
}) {
  const cabeca = useQuery({
    queryKey: [...chave, 'cabeca'],
    queryFn: () => buscar(null),
    enabled: ligado,
    refetchInterval: intervalo,
    refetchOnWindowFocus: true,
    retry: false,
  })
  // A cauda é desta chave (organização, filtro, conversa): trocar a chave a recolhe.
  const chaveTxt = JSON.stringify(chave)
  const [cauda, setCauda] = useState<{ chave: string; inicio: string } | null>(null)
  const inicio = cauda?.chave === chaveTxt ? cauda.inicio : null
  const anteriores = useInfiniteQuery({
    queryKey: [...chave, 'cauda', inicio],
    queryFn: ({ pageParam }) => buscar(pageParam),
    initialPageParam: inicio as string,
    getNextPageParam: (ultima: P) => ultima.proximo,
    enabled: ligado && inicio != null,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const daCabeca = cabeca.data?.items ?? []
  const vistos = new Set(daCabeca.map(idDe))
  const daCauda = inicio == null ? [] : (anteriores.data?.pages ?? []).flatMap((p) => p.items)
  return {
    cabeca: cabeca.data,
    itens: [...daCabeca, ...daCauda.filter((i) => !vistos.has(idDe(i)))],
    carregando: cabeca.isLoading,
    erro: cabeca.error,
    temMais: inicio == null ? !!cabeca.data?.proximo : !!anteriores.hasNextPage,
    carregandoMais: inicio != null && (anteriores.isLoading || anteriores.isFetchingNextPage),
    paginasAnteriores: inicio == null ? [] : anteriores.data?.pages ?? [],
    carregarMais: () => {
      if (inicio == null) {
        if (cabeca.data?.proximo) setCauda({ chave: chaveTxt, inicio: cabeca.data.proximo })
      } else {
        void anteriores.fetchNextPage()
      }
    },
  }
}

export function useConversas(orgId: number | undefined, gateway: string | null) {
  return useCabecaECauda<ListaDeConversas, ConversaWhatsApp>({
    // A lista junta os gateways página a página (cada um tem o seu cursor no WhatsApi): entre
    // páginas, a ordem pela última mensagem vale dentro de cada gateway.
    chave: ['whatsapi-conversas', orgId, 'lista', gateway],
    buscar: (antesDe) => whatsapiOrgApi.conversas(orgId as number, {
      ...(gateway ? { gateway } : {}),
      ...(antesDe ? { antes_de: antesDe } : {}),
    }),
    intervalo: RELEITURA_DA_LISTA_MS,
    ligado: orgId != null,
    idDe: (c) => `${c.gateway_id}/${c.ref}`,
  })
}

export function useMensagensDaConversa(orgId: number | undefined, gwId: string | null, ref: string | null) {
  return useCabecaECauda<PaginaDeMensagens, MensagemDaConversa>({
    chave: ['whatsapi-conversas', orgId, 'mensagens', gwId, ref],
    buscar: (antesDe) => whatsapiOrgApi.mensagensDaConversa(
      orgId as number, gwId as string, ref as string, antesDe ?? undefined),
    intervalo: RELEITURA_DA_CONVERSA_MS,
    ligado: orgId != null && gwId != null && ref != null,
    idDe: (m) => m.id,
  })
}

/** Depois de agir (ou de uma recusa, que diz que a tela estava velha): relê a lista, a
 *  conversa, a seção "Atendimento humano" e a trilha do gateway. */
function useRecarregar(orgId: number, gwId: string) {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['whatsapi-conversas', orgId] })
    void qc.invalidateQueries({ queryKey: ['wagw-bot-conversas', orgId, gwId] })
    void qc.invalidateQueries({ queryKey: ['wagw-trilha', orgId, gwId] })
  }
}

function avisarErro(e: unknown) {
  notifications.show({ color: 'red', title: 'Não foi possível concluir', message: mensagemDeErro(e) })
}

export function useAssumirConversa(orgId: number, gwId: string) {
  const recarregar = useRecarregar(orgId, gwId)
  return useMutation({
    mutationFn: (ref: string) => whatsapiOrgApi.assumirConversa(orgId, gwId, ref),
    onSettled: recarregar,
    onError: avisarErro,
  })
}

export function useResponderConversa(orgId: number, gwId: string) {
  const recarregar = useRecarregar(orgId, gwId)
  return useMutation({
    mutationFn: ({ ref, texto }: { ref: string; texto: string }) =>
      whatsapiOrgApi.responderConversa(orgId, gwId, ref, texto),
    onSettled: recarregar,
    onError: avisarErro,
  })
}
